// Authentication API functions
import { AUTH_API_BASE_URL } from './apiConfig';

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  success: boolean;
  token?: string;
  user?: {
    id?: string;
    username?: string;
    name?: string;
    role?: string;
  };
  message?: string;
}

export interface SetPasswordRequest {
  username: string; // Format: name_surname
  password: string;
}

export interface SetPasswordResponse {
  success: boolean;
  message?: string;
}

export const authApi = {
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    // Dev-only shortcut: bypass backend for demo users
    if (process.env.NODE_ENV === 'development') {
      const { username, password } = credentials;
      const isUserDemo = username === 'user' && password === 'user';
      const isAdminDemo = username === 'admin' && password === 'admin';

      if (isUserDemo || isAdminDemo) {
        const role = isAdminDemo ? 'admin' : 'user';
        console.log(`[DEV] Auto-authenticated demo ${role}:`, username);
        return {
          success: true,
          token: 'dev-demo-token',
          user: {
            id: isAdminDemo ? 'admin-demo' : 'user-demo',
            username,
            name: username,
            role,
          },
        };
      }
    }

    try {
      const response = await fetch(`${AUTH_API_BASE_URL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      const data = await response.json();
      
      // Debug: Log the raw response from MB backend
      console.log('Raw MB backend response:', data);
      
      // MB returns { accessToken, user: { id, username, role }, mustChangePassword? } on success
      // or { error: { code, message, requestId } } on error
      if (response.status === 200 && data.accessToken) {
        const loginResponse = {
          success: true,
          token: data.accessToken,
          user: {
            id: data.user.id,
            username: data.user.username,
            name: data.user.username, // MB doesn't return name, use username
            role: data.user.role, // Include role from MB backend
          },
        };
        
        // Debug: Log what we're returning
        console.log('Parsed login response:', loginResponse);
        console.log('User role:', loginResponse.user.role);
        
        return loginResponse;
      } else if (response.status === 401) {
        return {
          success: false,
          message: data.error?.message || 'Invalid username or password',
        };
      } else {
        return {
          success: false,
          message: data.error?.message || 'Login failed. Please try again.',
        };
      }
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        message: 'Network error. Please try again.',
      };
    }
  },

  async setPassword(credentials: SetPasswordRequest): Promise<SetPasswordResponse> {
    try {
      const response = await fetch(`${AUTH_API_BASE_URL}/set-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      const data = await response.json();

      if (response.status === 200 && data.success) {
        return {
          success: true,
          message: data.message || 'Password set successfully',
        };
      } else {
        return {
          success: false,
          message: data.error?.message || 'Failed to set password. Please try again.',
        };
      }
    } catch (error) {
      console.error('Set password error:', error);
      return {
        success: false,
        message: 'Network error. Please try again.',
      };
    }
  },
}; 